import React from 'react';
import { Helmet } from 'react-helmet-async';
import Grid from '@material-ui/core/Grid';
import useEntries from '../Common/useEntries';
import ClientList from './ClientList';
import {
    PageHero,
    Section,
    SectionHeading,
    LoadingState,
    EmptyState,
    LinkCard,
} from '../Common/PageLayout';
import PublicIcon from '@material-ui/icons/Public';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';

const breadcrumbs = [
    { label: 'About the Ministry', to: '/profile' },
    { label: 'Partners' },
];

/** Overview of the institutions the Ministry works with, plus their logos. */
export default function PartnersPage() {
    const { items, loading } = useEntries({
        content_type: 'client-partners',
        limit: 120,
    });

    return (
        <>
            <Helmet>
                <title>Partners | Ministry of Finance, Timor-Leste</title>
                <meta
                    name="description"
                    content="Development partners, international financial institutions and government bodies that work with the Ministry of Finance of Timor-Leste."
                />
            </Helmet>

            <PageHero
                eyebrow="About the Ministry"
                title="Partners"
                description="Strengthening public financial management is a shared effort. The Ministry cooperates with bilateral and multilateral partners, financial institutions and fellow government bodies across planning, budgeting, revenue and reporting."
                breadcrumbs={breadcrumbs}
            />

            <Section>
                <SectionHeading
                    overline="How we cooperate"
                    title="Areas of cooperation"
                    description="Partnerships support the Ministry's core functions and are reported on through the national budget and aid transparency processes."
                />
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <LinkCard
                            icon={<AccountBalanceIcon />}
                            title="Development assistance"
                            description="How external assistance is mobilised, recorded and reported alongside the State Budget."
                            to="/aidpage"
                            cta="Aid and development"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <LinkCard
                            icon={<DescriptionOutlinedIcon />}
                            title="Reports and agreements"
                            description="Budget books, aid transparency reporting and other published documents from the Ministry."
                            to="/publications"
                            cta="Browse publications"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <LinkCard
                            icon={<PublicIcon />}
                            title="Economy and public finance"
                            description="Macroeconomic context, fiscal policy and the data that underpins the Ministry's planning."
                            to="/economypage"
                            cta="Economy and aid"
                        />
                    </Grid>
                </Grid>
            </Section>

            <Section tone="muted" bordered>
                <SectionHeading
                    overline="Institutions"
                    title="Partner institutions"
                    description="Select a logo to visit the partner's own website."
                    rule
                />

                {loading && <LoadingState label="Loading partner institutions" />}

                {!loading && items.length === 0 && (
                    <EmptyState
                        title="Partner list unavailable"
                        description="Partner information could not be loaded right now. Please try again later."
                    />
                )}

                {!loading && items.length > 0 && (
                    <Grid container spacing={3}>
                        {items.map((entry) => (
                            <Grid item xs={6} sm={4} md={3} lg={2} key={entry.sys.id}>
                                <ClientList menus={entry} />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Section>
        </>
    );
}
